import { useCallback } from "react";
import {
  RedirectionSideEffect,
  TransformData,
  useCreate,
  useNotify,
  useRedirect,
  useResourceDefinition,
  useTranslate,
} from "react-admin";

const useCreateWithValidation = (
  resource: string,
  {
    redirectTo = "list",
    transform,
  }: { redirectTo: RedirectionSideEffect; transform?: TransformData }
) => {
  const { hasEdit, hasShow } = useResourceDefinition({ resource });
  const finalRedirectTo =
    redirectTo ?? getDefaultRedirectRoute(hasShow, hasEdit);
  const translate = useTranslate();
  const redirect = useRedirect();
  const notify = useNotify();
  const [create] = useCreate();
  const save = useCallback(
    async (values: any) => {
      const transformedValues = await Promise.resolve(
        transform ? transform(values) : values
      );

      try {
        const data = await create(
          resource,
          { data: transformedValues },
          { returnPromise: true }
        );
        notify("ra.notification.created", {
          type: "info",
          messageArgs: { smart_count: 1 },
        });
        redirect(finalRedirectTo, resource, data.id, data);
      } catch (error: any) {
        const fieldErrors = error?.body?.graphQLErrors?.filter(
          (err: any) => err?.extensions?.code
        );
        if (fieldErrors?.length) {
          // Reduce fieldErrors to an object with extensions field as key and code as value
          const validationErrors = fieldErrors.reduce(
            (acc: Object, err: any) => ({
              ...acc,
              [err?.extensions?.field]: translate(
                `error.${err?.extensions?.code ?? "invalid"}`
              ),
            }),
            {}
          );
          return validationErrors;
        }
        notify(
          typeof error === "string"
            ? error
            : error.message || "ra.notification.http_error",
          {
            type: "warning",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                  ? error.message
                  : undefined,
            },
          }
        );
      }
    },
    [create, notify, redirect, resource, translate, finalRedirectTo, transform]
  );

  return save;
};

export default useCreateWithValidation;

const getDefaultRedirectRoute = (hasShow?: boolean, hasEdit?: boolean) => {
  if (hasEdit) {
    return "edit";
  }
  if (hasShow) {
    return "show";
  }
  return "list";
};
